interface FilterState {
  dateFrom: string;
  dateTo: string;
  supplier: string;
  customer: string;
  site: string;
  currency: string;
}

interface SaveFilterDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  filterName: string;
  setFilterName: (name: string) => void;
  filterDescription: string;
  setFilterDescription: (desc: string) => void;
  isDefault: boolean;
  setIsDefault: (val: boolean) => void;
  isShared: boolean;
  setIsShared: (val: boolean) => void;
  filters: FilterState;
  suppliers: Array<{ id: number; name: string }>;
  customers: Array<{ id: number; name: string }>;
  sites: Array<{ id: number; name: string }>;
}

export function SaveFilterDialog({
  isOpen,
  onClose,
  onSave,
  filterName,
  setFilterName,
  filterDescription,
  setFilterDescription,
  isDefault,
  setIsDefault,
  isShared,
  setIsShared,
  filters,
  suppliers,
  customers,
  sites,
}: SaveFilterDialogProps) {
  if (!isOpen) return null;

  const handleClose = () => {
    setFilterName('');
    setFilterDescription('');
    setIsShared(false);
    setIsDefault(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-foreground">Save Filter Preset</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          {/* Filter Name */}
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">
              Filter Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder="e.g., Q1 2024 Supplier Review"
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-semibold mb-2 block text-foreground">
              Description (Optional)
            </label>
            <textarea
              value={filterDescription}
              onChange={(e) => setFilterDescription(e.target.value)}
              placeholder="Brief description of this filter preset..."
              rows={3}
              className="w-full px-3 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background resize-none"
            />
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
              />
              <span className="text-sm text-foreground">
                Set as my default filter
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isShared}
                onChange={(e) => setIsShared(e.target.checked)}
                className="h-4 w-4 text-primary focus:ring-primary border-border rounded"
              />
              <span className="text-sm text-foreground">
                Share with my company
              </span>
            </label>
          </div>

          {/* Current Filters Preview */}
          <div className="bg-muted rounded-md p-3">
            <p className="text-xs font-semibold text-foreground mb-2">Current Filters:</p>
            <div className="text-xs text-muted-foreground space-y-1">
              {filters.dateFrom && <p>From: {filters.dateFrom}</p>}
              {filters.dateTo && <p>To: {filters.dateTo}</p>}
              {filters.supplier && (
                <p>Supplier: {suppliers.find(s => s.id.toString() === filters.supplier)?.name || filters.supplier}</p>
              )}
              {filters.customer && (
                <p>Company: {customers.find(c => c.id.toString() === filters.customer)?.name || filters.customer}</p>
              )}
              {filters.site && (
                <p>Company: {sites.find(s => s.id.toString() === filters.site)?.name || filters.site}</p>
              )}
              <p>Currency: {filters.currency}</p>
            </div>
          </div>
        </div>

        {/* Dialog Actions */}
        <div className="px-6 py-4 border-t flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Save Filter
          </button>
        </div>
      </div>
    </div>
  );
}
